import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of, from } from 'rxjs';
import { Router } from '@angular/router';
import { tap, catchError, switchMap, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  
  private userProfileSubject = new BehaviorSubject<any>(this.getSavedProfile());
  public userProfile$ = this.userProfileSubject.asObservable();
  
  // Flag to track if a refresh is in progress
  private refreshInProgress = false;
  // Subject to return the new token to multiple subscribers
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  
  // Handle refresh token operation timestamp to prevent multiple refreshes
  private lastRefreshAttempt = 0;
  // Minimum time between refresh attempts in milliseconds (30 seconds)
  private readonly minRefreshInterval = 30000;
  
  constructor(
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Check token validity on service initialization
    this.checkAuthentication();
    
    // Add an event listener for page visibility changes
    this.setupVisibilityListener();
    
    // Start continuous token refresh mechanism
    this.startContinuousTokenRefresh();
  }
  
  // Set up an event listener to detect when the page becomes visible again
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      // When the page becomes visible again after being hidden
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, checking authentication...');
        // Check token validity and refresh if needed
        if (this.isLoggedIn() && this.isTokenExpired(300000)) {
          console.log('Token needs refresh after page visibility change');
          this.refreshToken().subscribe({
            next: token => {
              if (token) {
                console.log('Token refreshed after visibility change');
              } else {
                console.warn('Token refresh failed after visibility change');
              }
            },
            error: err => {
              console.error('Error refreshing token after visibility change:', err);
            }
          });
        }
      }
    });
  }
  
  // Check if there's a valid token
  private hasValidToken(): boolean {
    return !!localStorage.getItem('access_token');
  }
  
  // Get token expiration time - returns undefined if can't decode
  private getTokenExpiration(token: string | null): number | undefined {
    if (!token) return undefined;
    
    try {
      // Parse the JWT token
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return undefined;
      
      const payload = JSON.parse(atob(tokenParts[1]));
      
      // 'exp' claim is standard for JWT expiration timestamp
      if (payload && payload.exp) {
        return payload.exp * 1000; // Convert to milliseconds
      }
      
      return undefined;
    } catch (e) {
      console.error('Error parsing token:', e);
      return undefined;
    }
  }
  
  // Check if the token is expired or will expire soon (within buffer time)
  isTokenExpired(bufferTime = 60000): boolean {
    const token = localStorage.getItem('access_token');
    if (!token) return true;
    
    const expiration = this.getTokenExpiration(token);
    if (!expiration) return false; // Can't determine, assume not expired
    
    // Check if token is expired or will expire within the buffer time
    const now = Date.now();
    
    return now >= (expiration - bufferTime);
  }
  
  // Get a valid token - refreshes if needed
  getValidToken(): Observable<string | null> {
    const token = localStorage.getItem('access_token');
    
    // If no token, return null
    if (!token) {
      return of(null);
    }
    
    // If token will expire soon, refresh it
    if (this.isTokenExpired()) {
      return this.refreshToken();
    }
    
    // If token is still valid, return it
    return of(token);
  }
  
  // Get saved profile from localStorage - always returns object with roles array to prevent crashes
  private getSavedProfile(): any {
    try {
      const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
      return profile && Array.isArray(profile.roles) ? profile : { roles: [] };
    } catch {
      return { roles: [] };
    }
  }
  
  // Check authentication status and update subjects
  checkAuthentication(): void {
    const isAuthenticated = this.hasValidToken();
    this.isAuthenticatedSubject.next(isAuthenticated);
    
    if (isAuthenticated) {
      this.userProfileSubject.next(this.getSavedProfile());
    }
  }
  
  // Set authentication data after successful login
  setAuthData(token: string, refreshToken: string | null, profile: any): void {
    // Run inside NgZone to ensure change detection
    this.ngZone.run(() => {
      localStorage.setItem('access_token', token);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      localStorage.setItem('user_profile', JSON.stringify(profile));
      
      this.isAuthenticatedSubject.next(true);
      this.userProfileSubject.next(profile);
      
      // Reset refresh token state
      this.refreshInProgress = false;
      this.refreshTokenSubject.next(null);
      
      // Force a check authentication
      this.checkAuthentication();
      
      console.log('Auth data set successfully, authenticated:', this.isLoggedIn());
    });
  }
  
  // Clear authentication data on logout
  clearAuthData(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('last_token_refresh');
    localStorage.removeItem('last_user_activity');
    
    this.isAuthenticatedSubject.next(false);
    this.userProfileSubject.next({ roles: [] });
    this.refreshTokenSubject.next(null);
    this.refreshInProgress = false;
  }
  
  // Get user profile from API
  fetchUserProfile(): Observable<any> {
    return this.http.get(environment.apiUrl + '/api/me').pipe(
      tap(profile => {
        localStorage.setItem('user_profile', JSON.stringify(profile));
        this.userProfileSubject.next(profile);
      })
    );
  }
  
  // Check if user is logged in
  isLoggedIn(): boolean {
    return this.hasValidToken();
  }
  
  // Get current user profile
  getUserProfile(): any {
    return this.userProfileSubject.value;
  }
  
  // Refresh token - returns Observable of the new access token
  refreshToken(): Observable<string | null> {
    // If a refresh is already in progress, return the subject
    if (this.refreshInProgress) {
      console.log('Refresh already in progress, returning cached observable');
      return this.refreshTokenSubject.asObservable();
    }
    
    // Throttle refresh attempts
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.minRefreshInterval) {
      console.log('Too many refresh attempts, throttling');
      return of(localStorage.getItem('access_token'));
    }
    
    // Update last refresh attempt timestamp
    this.lastRefreshAttempt = now;
    
    // If no refresh token exists, clear auth and redirect
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.log('No refresh token available');
      this.clearAuthData();
      this.router.navigate(['/login']);
      return of(null);
    }
    
    // Mark refresh as in progress
    this.refreshInProgress = true;
    
    // Reset the subject to null
    this.refreshTokenSubject.next(null);
    
    console.log('Starting token refresh process...');
    
    const tenantId = environment.azureTenantId;
    const clientId = environment.backendClientId;
    const redirectUri = window.location.origin + '/login';
    
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('client_id', clientId);
    body.set('scope', `api://${clientId}/user_impersonation openid profile email`);
    body.set('refresh_token', refreshToken);
    body.set('redirect_uri', redirectUri);
    
    // Using fetch API to avoid interceptors and prevent infinite loop
    return from(fetch(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Refresh token failed: ' + response.status);
      }
      return response.json();
    })
    .then(data => {
      console.log('Token refresh response received');
      
      // Store the new tokens
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        
        // Update auth status
        this.isAuthenticatedSubject.next(true);
        
        // Notify subscribers with the new token
        this.refreshTokenSubject.next(data.access_token);
        
        // Return the new token
        return data.access_token;
      } else {
        throw new Error('No access token in refresh response');
      }
    })
    .catch(error => {
      console.error('Token refresh error:', error);
      // If refresh fails, clear auth and redirect to login if not already there
      this.clearAuthData();
      
      const currentUrl = window.location.pathname;
      if (currentUrl !== '/login') {
        this.router.navigate(['/login'], { queryParams: { expired: 'true' } });
      }
      
      return null;
    }))
    .pipe(
      finalize(() => {
        // Mark refresh as no longer in progress
        this.refreshInProgress = false;
      })
    );
  }
  
  // Handle HTTP errors with token refresh
  handleAuthError(error: HttpErrorResponse, retryRequest: Function): Observable<any> {
    // Only handle 401 errors
    if (error.status !== 401) {
      return throwError(() => error);
    }
    
    // Try to refresh the token
    return this.refreshToken().pipe(
      switchMap(token => {
        // If token refresh successful, retry the request
        if (token) {
          return retryRequest();
        }
        // If token refresh failed, propagate the error
        return throwError(() => error);
      }),
      catchError(refreshError => {
        console.error('Error refreshing token:', refreshError);
        return throwError(() => error);
      })
    );
  }
  
  // Logout user
  logout(): void {
    // Run inside NgZone to ensure change detection and navigation works properly
    this.ngZone.run(() => {
      // Clear auth data first
      this.clearAuthData();
      
      // Log the logout action
      console.log('User logged out, redirecting to Microsoft login');
      
      // Redirect directly to Microsoft login
      const tenantId = environment.azureTenantId;
      const clientId = environment.backendClientId;
      const redirectUri = encodeURIComponent(window.location.origin + '/');
      
      // Build Microsoft logout URL that will redirect back to login
      const logoutUrl = 
        `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?` +
        `post_logout_redirect_uri=${redirectUri}`;
      
      // Redirect to Microsoft logout
      window.location.href = logoutUrl;
    });
  }
  
  /**
   * Sets up a continuous token refresh mechanism to keep the session alive
   * as long as the user is active within refresh token's lifetime
   */
  private startContinuousTokenRefresh(): void {
    // Only proceed if we're logged in
    if (!this.isLoggedIn()) {
      return;
    }
    
    console.log('Starting continuous token refresh mechanism');
    
    // Store the last refresh timestamp
    let lastRefreshTime = localStorage.getItem('last_token_refresh');
    if (!lastRefreshTime) {
      lastRefreshTime = Date.now().toString();
      localStorage.setItem('last_token_refresh', lastRefreshTime);
    }
    
    const checkAndRefreshToken = () => {
      // Skip if not logged in
      if (!this.isLoggedIn()) {
        return;
      }
      
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      try {
        // Get token expiration time
        const expiration = this.getTokenExpiration(token);
        if (!expiration) return;
        
        const now = Date.now();
        const timeUntilExpiry = expiration - now;
        
        // If token expires in less than 30 minutes (or is expired), refresh it
        if (timeUntilExpiry < 1800000) {
          console.log(`Token expires in ${Math.floor(timeUntilExpiry/60000)} minutes. Refreshing...`);
          
          this.refreshToken().subscribe({
            next: token => {
              if (token) {
                console.log('Token refreshed successfully in background');
                localStorage.setItem('last_token_refresh', Date.now().toString());
              }
            },
            error: err => {
              console.error('Error refreshing token in background:', err);
            }
          });
        } else {
          // Reduce logging frequency for production
          if (timeUntilExpiry % 3600000 < 60000) { // Log approximately once per hour
            console.log(`Token valid for ${Math.floor(timeUntilExpiry/60000)} more minutes. No refresh needed yet.`);
          }
        }
      } catch (e) {
        console.error('Error in continuous token refresh:', e);
      }
    };
    
    // Run the first check
    checkAndRefreshToken();
    
    // Check every 30 minutes instead of 15 to reduce resource usage
    // This is still frequent enough for most token lifetimes
    const intervalId = setInterval(checkAndRefreshToken, 30 * 60 * 1000);
    
    // Store interval ID to potentially clear it later if needed
    (window as any).__tokenRefreshInterval = intervalId;
    
    // Debounce function to limit how often the activity handler fires
    let activityTimeout: any = null;
    const debouncedActivityHandler = () => {
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      
      activityTimeout = setTimeout(() => {
        const lastActivity = localStorage.getItem('last_user_activity');
        const now = Date.now().toString();
        
        // If it's been more than 20 minutes since last activity check (extended from 10)
        if (!lastActivity || (parseInt(now) - parseInt(lastActivity)) > 20 * 60 * 1000) {
          localStorage.setItem('last_user_activity', now);
          checkAndRefreshToken();
        }
        
        activityTimeout = null;
      }, 1000); // 1 second debounce
    };
    
    // Use fewer event listeners to reduce overhead
    // Just track clicks and key presses, which are the most meaningful user interactions
    document.addEventListener('click', debouncedActivityHandler);
    document.addEventListener('keypress', debouncedActivityHandler);
    
    // Don't track mousemove or touch events as they fire too frequently
    // and don't necessarily indicate meaningful user activity
  }
} 

